import { useState } from 'react';
import AuthProvider from '../services/auth/AuthProvider/Index';
import { FormErrorsSchema } from '../types';
import { mergeErrors } from '../utils';
import notifications from '../services/notifications';

interface TwoStepConfig<TRequestBody, TConfirmBody> {
  requestUrl: string;
  confirmUrl: string;
  buildRequestBody: () => TRequestBody;
  buildConfirmBody: () => TConfirmBody;
  requestSuccessMessage: string;
  confirmSuccessMessage: string;
  onRequestSuccess?: () => void;
  onConfirmSuccess?: () => Promise<void> | void;
  genericErrorPrefix?: string;
}

/**
 * Shared two-step change workflow (request + token confirmation).
 */
export const useTwoStepChange = <TRequestBody, TConfirmBody>({
  requestUrl,
  confirmUrl,
  buildRequestBody,
  buildConfirmBody,
  requestSuccessMessage,
  confirmSuccessMessage,
  onRequestSuccess,
  onConfirmSuccess,
  genericErrorPrefix = 'Something went wrong while processing your request.',
}: TwoStepConfig<TRequestBody, TConfirmBody>) => {
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});

  const handleRequest = async () => {
    try {
      setLoading(true);
      setFormErrors({});

      const response = await fetch(requestUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
        },
        method: 'POST',
        body: JSON.stringify(buildRequestBody()),
      });

      if (!response.ok) {
        if (response.status === 422) {
          const errorData = await response.json();
          setFormErrors(mergeErrors(errorData.errors));
        } else {
          throw new Error('Failed to load resource');
        }
        return;
      }

      notifications.success({ message: requestSuccessMessage });
      setRequested(true);
      onRequestSuccess?.();
    } catch (error) {
      console.error('Error requesting change:', error);
      notifications.error({ message: genericErrorPrefix });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setFormErrors({});

      const response = await fetch(confirmUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
        },
        method: 'PATCH',
        body: JSON.stringify(buildConfirmBody()),
      });

      if (!response.ok) {
        if (response.status === 422) {
          const errorData = await response.json();
          setFormErrors(mergeErrors(errorData.errors));
        } else {
          throw new Error('Failed to load resource');
        }
        return;
      }

      notifications.success({ message: confirmSuccessMessage });
      setRequested(false);
      await onConfirmSuccess?.();
    } catch (error) {
      console.error('Error confirming change:', error);
      notifications.error({ message: genericErrorPrefix });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    requested,
    formErrors,
    setFormErrors,
    handleRequest,
    handleConfirm,
    setRequested,
  };
};

